/**
 * EngagementHistogramSection — distribution chart with the creator highlighted.
 *
 * The histogram is Phyllo's percentile distribution across their tracked
 * profiles; one bin per percentile band. Creator's own engagement_rate
 * is mapped into whichever bin contains it (bin painted Brand purple); the
 * median bin is marked with a gray dashed guide line.
 *
 * Layout mirrors Figma node 1672:33202: header (title + comparison count on the
 * left, creator's rate + peer rank on the right), the chart, a legend, a
 * divider, and a summary sentence.
 *
 * Hidden when the histogram is empty.
 */
import { useMemo } from 'react';
import type { InsightIQProfile } from '@discovr/types';
import { HistogramBars, type HistogramBin } from '../charts/HistogramBars';
import { InfoTip } from '../charts/InfoTip';
import { fmtDecimal2 } from '../utils/formatters';

interface EngagementHistogramSectionProps {
  profile: InsightIQProfile;
}

/** Engagement-rate fraction → percent number string, fixed 2dp. Bucket
 *  boundaries (not a measured stat) so 0 is a legitimate value, not hidden. */
function fmtErNum(fraction: number): string {
  return (fraction * 100).toFixed(2);
}

export function EngagementHistogramSection({ profile }: EngagementHistogramSectionProps) {
  // Memoized so the `?? []` fallback doesn't hand `bins` a fresh array
  // reference every render (which would defeat its useMemo below).
  const rawBins = useMemo(
    () => profile.engagement_rate_histogram ?? [],
    [profile.engagement_rate_histogram],
  );
  const creatorRate = profile.engagement_rate ?? null;
  const creatorRateDisplay = fmtDecimal2(creatorRate != null ? creatorRate * 100 : null);

  const bins: HistogramBin[] = useMemo(
    () =>
      rawBins.map((b) => ({
        min: b.min ?? null,
        max: b.max ?? null,
        totalProfileCount: b.total_profile_count,
        // Phyllo serializes is_median as the string "True" — coerce both
        // null and any other value to false.
        isMedian: String(b.is_median ?? '').toLowerCase() === 'true',
      })),
    [rawBins],
  );

  if (bins.length === 0) return null;

  const totalProfiles = bins.reduce((sum, b) => sum + b.totalProfileCount, 0);

  // Percentile = share of comparable profiles whose engagement-rate band sits
  // entirely below the creator's value (i.e. bins where max <= creatorRate).
  // Only derivable when we have a creator value AND a non-empty population —
  // otherwise we omit the "Top X%" line rather than fabricate a rank.
  const percentile =
    creatorRate != null && totalProfiles > 0
      ? Math.round(
          (bins
            .filter((b) => b.max != null && b.max <= creatorRate)
            .reduce((sum, b) => sum + b.totalProfileCount, 0) /
            totalProfiles) *
            100,
        )
      : null;
  // "Top X%" — the creator outranks `percentile`% of peers, so they're in the
  // top (100 − percentile)%. Clamp to [1, 100] so we never show "Top 0%".
  const topPct = percentile != null ? Math.min(100, Math.max(1, 100 - percentile)) : null;

  // Median band label for the legend, e.g. "217.5–252.78%".
  const medianBin = bins.find((b) => b.isMedian);
  const medianBand =
    medianBin && medianBin.min != null && medianBin.max != null
      ? `${fmtErNum(medianBin.min)}–${fmtErNum(medianBin.max)}%`
      : null;

  return (
    <div className="mb-4 flex flex-col gap-5 overflow-hidden rounded-2xl border border-border bg-card p-6">
      {/* Header: title + comparison count (left), creator rate + rank (right) */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="flex items-center gap-1.5 text-heading-h6/7 font-medium text-foreground">
            Engagement rate distribution
            <InfoTip label="What is engagement rate distribution?" size={16} widthClass="w-96">
              Engagement rate is how often a creator&rsquo;s audience interacts with their
              posts (likes plus comments) relative to how many followers they have. This chart
              lines this creator up against thousands of similar creators — each bar is how many
              of them land in a given engagement range. The highlighted bar is where this creator
              sits, and the dashed line marks the typical (median) creator. Further right means
              more engaged than peers.
            </InfoTip>
          </h3>
          <p className="text-body-sm-regular text-muted-foreground">
            This creator vs {totalProfiles.toLocaleString('en-US')} comparable profiles
          </p>
        </div>
        {creatorRateDisplay != null && (
          <div className="flex flex-col items-end gap-1">
            <span className="text-heading-h5/8 font-medium text-foreground">
              {creatorRateDisplay}%
            </span>
            {topPct != null && (
              <span className="text-body-sm-semibold text-(--brand-600)">
                Top {topPct}% of peers
              </span>
            )}
          </div>
        )}
      </div>

      <HistogramBars bins={bins} creatorValue={creatorRate} />

      {/* Legend */}
      <div className="flex items-center gap-6 pt-0.5">
        <div className="flex items-center gap-2">
          <span className="size-[9px] rounded-full bg-(--brand-600)" />
          <span className="text-body-sm-medium text-foreground">This creator</span>
        </div>
        {medianBand && (
          <div className="flex items-center gap-2">
            <span className="h-0 w-6 border-t border-dashed border-(--foreground-subtle)" />
            <span className="text-body-sm-medium text-muted-foreground">Median {medianBand}</span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px w-full bg-[#f5f5f5]" />

      {/* Summary sentence (replaces a legend caption). */}
      {topPct != null && (
        <p className="text-body-sm-regular text-muted-foreground">
          <span className="font-bold text-foreground">
            {topPct <= 50 ? 'Well above the median.' : 'Around the median.'}
          </span>{' '}
          Engagement places this creator in the top {topPct}% of{' '}
          {totalProfiles.toLocaleString('en-US')} similar profiles.
        </p>
      )}
    </div>
  );
}
